import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInfiniteList } from '@/hooks/useInfiniteList';

import { protocolosApi } from '../api';
import type {
  Protocolo,
  ProtocoloCreate,
  ProtocoloListParams,
  ProtocoloUpdate,
} from '../types';

const QUERY_KEY_BASE = ['protocolos'] as const;

export const protocolosKeys = {
  all: QUERY_KEY_BASE,
  list: (params: ProtocoloListParams) => [...QUERY_KEY_BASE, 'list', params] as const,
  detail: (uid: string) => [...QUERY_KEY_BASE, uid] as const,
};

export function useProtocolos(params: ProtocoloListParams = {}) {
  return useInfiniteList({
    queryKey: protocolosKeys.list(params),
    queryFn: ({ signal, ...rest }) => protocolosApi.list({ ...rest, signal }),
    params,
  });
}

export function useProtocolo(uid: string | undefined) {
  return useQuery({
    queryKey: uid ? protocolosKeys.detail(uid) : protocolosKeys.all,
    queryFn: ({ signal }) => protocolosApi.getById(uid!, signal),
    enabled: !!uid,
  });
}

export function useCreateProtocolo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProtocoloCreate) => protocolosApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.all });
    },
  });
}

export function useUpdateProtocolo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, data }: { uid: string; data: ProtocoloUpdate }) =>
      protocolosApi.update(uid, data),
    onSuccess: (updated: Protocolo) => {
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.all });
      queryClient.setQueryData(protocolosKeys.detail(updated.uid_protocolo), updated);
    },
  });
}

export function useDeleteProtocolo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => protocolosApi.remove(uid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.all });
    },
  });
}
